import { useRef, useState, useMemo } from "react";
import { Menu } from "@mui/material";
import { Typography, Card, CardContent, Box, Chip, Divider, IconButton } from "@mui/material";
import SaveButton from "../../../components/buttons/SaveButton";
import { getIntlTranslation } from "../../../utils/helper";
import ReportIcon from '@mui/icons-material/Description';
import WarningIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { getStatus, fmtDE } from "../utils/statusUtils";
import { parseDefectsString } from "../utils/defectUtils";
import { useIntl } from "react-intl";

const ElementCard = ({ element, handleViewClick }) => {

    const intl = useIntl();
    const [expanded, setExpanded] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const expandBtnRef = useRef(null);
    const status = useMemo(
        () =>
            getStatus({
                nextStr: element?.nextInspectionDate,
                lastStr: element?.lastInspectionDate,
                protocolUploaded: element?.protocolUploaded,
            }),
        [element]
    );
    const defectsText = element?.defectsFound ?? "";
    const hasDefects = !!(defectsText && String(defectsText).trim().length);

    const parsedDefects = useMemo(
        () => (parseDefectsString(defectsText)),
        [defectsText]
    );

    if (!element) return null;

    return (
        <Card
            sx={{
                mb: 2,
                boxShadow: 4,
                borderRadius: 3,
                width: { xs: '100%', sm: 280, md: 320, lg: 340 },
                maxWidth: '100%',
                height: 260,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                position: 'relative',
                background: '#f9f9fb',
                overflow: 'hidden',
            }}
        >
            <CardContent sx={{ pb: 2 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <ReportIcon sx={{ color: '#254a9a', fontSize: 28 }} />
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#254a9a' }}>
                            {element.elementName}
                        </Typography>
                    </Box>
                    <Chip
                        label={intl.formatMessage({ id: `mi.${status}` }) || "-"}
                        color={
                            status === "upcoming" ? "warning" :
                                status === "overdue" ? "error" : "success"
                        }
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box mb={1}>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                        <strong>{getIntlTranslation("mi.last-inspection")}:</strong> {fmtDE(element.lastInspectionDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                        <strong>{getIntlTranslation("mi.next-inspection")}:</strong> {fmtDE(element.nextInspectionDate)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={0.5}>
                        <strong>{getIntlTranslation("mi.frequency")}:</strong> {element.frequencyYears || "-"}
                    </Typography>
                </Box>
                <Box mb={1}>
                    <Box mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                            {hasDefects ? (
                                <>
                                    <WarningIcon sx={{ color: '#aa3939', fontSize: 20 }} />
                                    <Typography variant="body2" color="error" sx={{ fontWeight: 500 }}>
                                        <strong>{getIntlTranslation("mi.defects-found")}</strong>
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <CheckCircleIcon sx={{ color: '#97b444', fontSize: 20 }} />
                                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                                        <strong>{getIntlTranslation("mi.no-defects-found")}</strong>
                                    </Typography>
                                </>
                            )}
                            {hasDefects && (
                                <IconButton
                                    size="small"
                                    ref={expandBtnRef}
                                    onClick={(e) => { setMenuAnchor(e.currentTarget); setExpanded(true); }}
                                    aria-label="expand"
                                >
                                    {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                        </Box>

                        {hasDefects && (
                            <Menu
                                anchorEl={menuAnchor}
                                open={expanded}
                                onClose={() => { setExpanded(false); setMenuAnchor(null); }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                PaperProps={{ sx: { minWidth: 240, maxHeight: 200, p: 0.5, pl: 2, pr: 1.5, borderRadius: 2, background: '#f5f7fa' } }}
                            >
                                {parsedDefects.length > 0 ? (
                                    parsedDefects.map((text, i) => (
                                        <Box key={i} sx={{ mb: 1 }}>
                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                • {text}
                                            </Typography>
                                        </Box>
                                    ))
                                ) : (
                                    <Typography sx={{ p: 1 }}>{getIntlTranslation("mi.no-defects-found")}</Typography>
                                )}
                            </Menu>
                        )}
                    </Box>

                </Box>
                <Box mb={2}>
                    <Typography variant="body2" color="text.secondary">
                        <strong>{getIntlTranslation("mi.reinspection-date")}: </strong> {element.reinspectionRequired ? element.reinspectionRequired : '-'}
                    </Typography>
                </Box>
            </CardContent>
            <Box sx={{ position: 'absolute', right: 16, bottom: 16 }}>
                <SaveButton
                    id="view-report"
                    buttonText="mi.view"
                    variant="button"
                    size="small"
                    style={{
                        padding: '4px 14px',
                        fontSize: '0.8rem',
                        minWidth: 'auto',
                        lineHeight: 1.25,
                        alignSelf: 'flex-end',
                        background: '#254a9a',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(37,74,154,0.12)'
                    }}
                    tooltip={{ title: getIntlTranslation('tooltip.view-site') }}
                    handleClick={(e) => {
                       handleViewClick()
                    }}
                />
            </Box>
        </Card>
    );
};

export default ElementCard;